export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    /** POST JSON `{ code, state }` — exchange OAuth authorization code for app tokens. */
    TOKEN: '/auth/sso/token',
    ME: '/users/me',
    /** GET — API 302 to Microsoft authorize (uses server ``AZURE_AD_*``; no UI env required). */
    AZURE_SSO_LOGIN: '/auth/sso/azure/login',
    /** POST JSON `{ email }` — send password-reset email; always returns 200 to prevent user enumeration. */
    FORGOT_PASSWORD: '/auth/forgot-password',
    /** GET `?token=<raw>` — validate a reset token without consuming it. */
    RESET_PASSWORD_VALIDATE: '/auth/reset-password/validate',
    /** POST JSON `{ token, new_password }` — validate token + send OTP to email. */
    RESET_PASSWORD: '/auth/reset-password',
    /** POST JSON `{ token, otp }` — verify OTP, set password, send confirmation email. */
    RESET_PASSWORD_VERIFY_OTP: '/auth/reset-password/verify-otp',
    CHANGE_PASSWORD: '/users/me/change-password',
  },
  COVERAGE: {
    LIST: '/coverage/articles',
    /** GET — option lists for the 5 backend-driven filter dropdowns. */
    FILTER_OPTIONS: '/coverage/filter-options',
    CRAWL_FAILURES: '/coverage/crawl-failures',
    CRAWL_FAILURES_RETRY: '/coverage/crawl-failures/retry',
    /** POST {ids: string[]} — re-queue with crawl_strategy='agent_llm' (Bedrock-Claude agent with fetch_url tool). */
    CRAWL_FAILURES_RETRY_WITH_LLM: '/coverage/crawl-failures/retry-with-llm',
    /** POST {rawText, title?, country?, date?, linkType?} — recover a permanently-failed crawl with analyst-supplied text. */
    CRAWL_FAILURES_SUBMIT_RAW_TEXT: (failureId: string) =>
      `/coverage/crawl-failures/${failureId}/submit-raw-text`,
    CRAWL_QUEUE: '/coverage/crawl-queue',
    DETAIL: (id: string) => `/coverage/articles/${id}`,
    RAW_HTML: (id: string) => `/coverage/articles/${id}/raw-html`,
    EXPORT: '/coverage/articles/export',
    EXPORT_MONTHLY: '/coverage/articles/export-monthly',
    LOCK: '/coverage/articles/lock',
    RETRY_FIELD_MAPPING: '/coverage/articles/retry-field-mapping',
    RETRY_TRANSLATION: '/coverage/articles/retry-translation',
    STATS: '/coverage/stats',
    DELETE: '/coverage/articles',
  },
  /** Article list + queue-for-import paths (backend: coverage + queue routers). */
  ARTICLES: {
    LIST: '/coverage/articles',
    DETAIL: (id: string) => `/coverage/articles/${id}`,
    /** POST JSON { urls: [{ url, country?, date?, note?, linkType? }, ...] } */
    IMPORT_QUEUE: '/queue',
    /** Alias of IMPORT_QUEUE */
    QUEUE: '/queue',
    /** Alias of IMPORT_QUEUE (batch URL enqueue) */
    BULK_IMPORT: '/queue',
    INGEST: '/queue',
    /** POST /queue — enqueue URL(s) for the crawl pipeline (same as IMPORT_QUEUE). */
    FETCH_URL: '/queue',
    /**
     * POST /queue — there is no separate HTML-import route yet.
     * Use `urls: [{ url, userRawText?, … }]` per backend `QueueUrlItem` when pasting body/transcript.
     */
    IMPORT_HTML: '/queue',
  },
  NEWSLETTERS: {
    LIST: '/newsletters',
    DETAIL: (id: string) => `/newsletters/${id}`,
    GENERATE: '/newsletters/generate',
    SUGGESTIONS: '/newsletters/suggestions',
    REVIEW: (id: string) => `/newsletters/${id}/review`,
    MONTHLY_LIST: '/newsletters/monthly',
    MONTHLY_CREATE: '/newsletters/monthly',
    MONTHLY_DETAIL: (id: string) => `/newsletters/monthly/${id}`,
    MONTHLY_UPDATE: (id: string) => `/newsletters/monthly/${id}`,
    MONTHLY_KEY_HIGHLIGHTS: '/newsletters/monthly/key-highlights',
    MONTHLY_KEY_HIGHLIGHTS_SEARCH: '/newsletters/monthly/key-highlights/search',
    MONTHLY_KEY_HIGHLIGHTS_RESOLVE: '/newsletters/monthly/key-highlights/resolve',
    MONTHLY_TOPIC_ARTICLES: '/newsletters/monthly/topic-articles',
    MONTHLY_EXACT_ARTICLE: '/newsletters/monthly/exact-article',
    MONTHLY_HEADER_IMAGE: '/newsletters/monthly/header-image',
    MONTHLY_SUBMIT_FOR_REVIEW: (id: string) =>
      `/newsletters/monthly/${id}/submit-for-review`,
    MONTHLY_START_REVIEW: (id: string) => `/newsletters/monthly/${id}/start-review`,
    MONTHLY_APPROVE: (id: string) => `/newsletters/monthly/${id}/approve`,
    MONTHLY_SEND_BACK: (id: string) => `/newsletters/monthly/${id}/send-back`,
    MONTHLY_PUBLISH: (id: string) => `/newsletters/monthly/${id}/publish`,
  },
  /**
   * Daily Newsletter endpoints — kept in a SEPARATE top-level block
   * (rather than nested under ``NEWSLETTERS``) so the daily module
   * never has to reach into the monthly endpoint table. Mirrors the
   * isolated backend package under
   * ``pr_insights_api.api.daily_newsletter``.
   */
  DAILY_NEWSLETTERS: {
    LIST: '/newsletters/daily',
    CREATE: '/newsletters/daily',
    DETAIL: (id: string) => `/newsletters/daily/${id}`,
    UPDATE: (id: string) => `/newsletters/daily/${id}`,
    SUBMIT_FOR_REVIEW: (id: string) =>
      `/newsletters/daily/${id}/submit-for-review`,
    START_REVIEW: (id: string) => `/newsletters/daily/${id}/start-review`,
    APPROVE: (id: string) => `/newsletters/daily/${id}/approve`,
    SEND_BACK: (id: string) => `/newsletters/daily/${id}/send-back`,
    PUBLISH: (id: string) => `/newsletters/daily/${id}/publish`,
    HEADER_IMAGE: '/newsletters/daily/header-image',
    BUBBLE_BUILD: '/newsletters/daily/bubble',
    BUBBLE_DELETE: (bubbleId: string) =>
      `/newsletters/daily/bubble/${bubbleId}`,
    ARTICLES: '/newsletters/daily/articles',
  },
  /**
   * Weekly Newsletter endpoints — kept in a SEPARATE top-level block
   * (rather than nested under ``NEWSLETTERS`` or ``DAILY_NEWSLETTERS``)
   * so the weekly module never has to reach into another cadence's
   * endpoint table. Mirrors the isolated backend package under
   * ``pr_insights_api.api.weekly_newsletter``.
   */
  WEEKLY_NEWSLETTERS: {
    LIST: '/newsletters/weekly',
    CREATE: '/newsletters/weekly',
    DETAIL: (id: string) => `/newsletters/weekly/${id}`,
    UPDATE: (id: string) => `/newsletters/weekly/${id}`,
    SUBMIT_FOR_REVIEW: (id: string) =>
      `/newsletters/weekly/${id}/submit-for-review`,
    START_REVIEW: (id: string) => `/newsletters/weekly/${id}/start-review`,
    APPROVE: (id: string) => `/newsletters/weekly/${id}/approve`,
    SEND_BACK: (id: string) => `/newsletters/weekly/${id}/send-back`,
    PUBLISH: (id: string) => `/newsletters/weekly/${id}/publish`,
    HEADER_IMAGE: '/newsletters/weekly/header-image',
    BUBBLE_BUILD: '/newsletters/weekly/bubble',
    BUBBLE_DELETE: (bubbleId: string) =>
      `/newsletters/weekly/bubble/${bubbleId}`,
    COVERAGE_STREAM: '/newsletters/weekly/coverage',
    COVERAGE_BUFFERED: '/newsletters/weekly/coverage-buffered',
    EDITORIAL_STREAM: '/newsletters/weekly/editorial-block',
    EDITORIAL_BUFFERED: '/newsletters/weekly/editorial-block-buffered',
  },
  NOTIFICATIONS: {
    LIST: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    READ_ALL: '/notifications/read-all',
  },
  TOPICS: {
    LIST: '/topics',
  },
  NEWSLETTER_TOPIC_CONFIG: {
    LIST: '/config/newsletter-topics',
    CREATE: '/config/newsletter-topics',
    UPDATE: (id: string) => `/config/newsletter-topics/${id}`,
    DELETE: (id: string) => `/config/newsletter-topics/${id}`,
    REORDER: '/config/newsletter-topics/reorder',
    SUB_TOPICS: (topicId: string) => `/config/newsletter-topics/${topicId}/sub-topics`,
    SUB_TOPIC: (topicId: string, subId: string) =>
      `/config/newsletter-topics/${topicId}/sub-topics/${subId}`,
    SUB_TOPICS_REORDER: (topicId: string) =>
      `/config/newsletter-topics/${topicId}/sub-topics/reorder`,
  },
  GOALS: {
    MONTHLY: '/goals/monthly',
    QUARTERLY: '/goals/quarterly',
    GLOBAL: '/goals/global',
    UPDATE: (id: string) => `/goals/${id}`,
  },
  CHAT: {
    SEND: '/chat/message',
    HISTORY: '/chat/history',
  },
  DASHBOARD: {
    STATS: '/dashboard/stats',
    RECENT: '/dashboard/recent-highlights',
  },
  SETTINGS: {
    TRIAGE_WEIGHTS: '/settings/triage-weights',
    TIER_LIST: '/settings/tier-list',
    TIER_LIST_BULK: '/settings/tier-list/bulk',
    BRANDS: '/settings/brands',
    BRANDS_BULK: '/settings/brands/bulk',
  },
  SOV: {
    GENERATE: '/sov',
    /** Server-rendered SVG (donut + legend) — embeddable in newsletters/PDFs. */
    CHART_SVG: '/sov/chart.svg',
    /** Server-rendered PNG (same chart) — required for Classic Outlook
     *  ``<img>`` rendering, which refuses SVG sources. */
    CHART_PNG: '/sov/chart.png',
    /** Saved monthly snapshots (history). */
    SNAPSHOTS: '/sov/snapshots',
    SNAPSHOT_DETAIL: (year: number, month: number) =>
      `/sov/snapshots/${year}/${month}`,
    SNAPSHOT_RECOMPUTE: (year: number, month: number) =>
      `/sov/snapshots/${year}/${month}/recompute`,
    /** SVG rendered from the *saved* snapshot (reflects in-place edits). */
    SNAPSHOT_CHART_SVG: (year: number, month: number) =>
      `/sov/snapshots/${year}/${month}/chart.svg`,
    /** PNG rendered from the *saved* snapshot — newsletter publish flow
     *  points the MSO ``<v:image>`` fallback at this URL because Classic
     *  Outlook refuses SVG ``<img>`` sources. */
    SNAPSHOT_CHART_PNG: (year: number, month: number) =>
      `/sov/snapshots/${year}/${month}/chart.png`,
  },
  IMPORT_AUDITS: {
    LIST: '/import-audits',
    DETAIL: (id: string) => `/import-audits/${id}`,
    DOWNLOAD: (id: string) => `/import-audits/${id}/download`,
    CRAWL_FAILURES: (id: string) => `/import-audits/${id}/crawl-failures`,
    ARTICLE_STATUS: (id: string) => `/import-audits/${id}/article-status`,
    IMPORT_EXCEL: '/queue/import-excel',
  },
  ADMIN: {
    USERS: '/management/users',
    USER_DETAIL: (id: string) => `/management/users/${id}`,
    USER_RESET_PASSWORD: (id: string) => `/management/users/${id}/reset-password`,
    USER_SEND_PASSWORD_SETUP: (id: string) => `/management/users/${id}/send-password-setup`,
    USER_DELETE_PERMANENT: (id: string) => `/management/users/${id}/permanent`,
    USER_UNLOCK: (id: string) => `/management/users/${id}/unlock`,
    EMAIL_DLS: '/management/email-dls',
    EMAIL_DL_DETAIL: (id: string) => `/management/email-dls/${id}`,
    EMAIL_SEND: '/management/email/send',
  },
  /** Developer tools → Redis/Valkey console. Developer-only on the backend. */
  REDIS: {
    ARTICLES: '/redis/articles',
    ARTICLE_MIRROR_STATS: '/redis/articles/mirror-stats',
    ARTICLE_REBUILD: '/redis/articles/rebuild',
    ARTICLE_DRIFT: (id: string) => `/redis/articles/${id}/drift`,
    ARTICLE_SYNC: (id: string) => `/redis/articles/${id}/sync`,
  },
} as const;

/** Alias for code that imports API_ENDPOINTS instead of ENDPOINTS. */
export const API_ENDPOINTS = ENDPOINTS;
